import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Bell, Mail, FileUp, Wallet, GitBranch, MessageCircle, Trash2, X, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { liveNotifications, deleteNotification, clearNotifications } from "../lib/api";
import useWebSocket from "../lib/useWebSocket";

const ICONS = {
    mail_sent: Mail,
    evrak_uploaded: FileUp,
    payment_added: Wallet,
    status_changed: GitBranch,
    email_parsed: Mail,
    sms_sent: MessageCircle,
    whatsapp_sent: MessageCircle,
};

const EVENT_LABELS = {
    mail_sent: "E-posta gönderildi",
    evrak_uploaded: "Evrak yüklendi",
    payment_added: "Ödeme kaydı",
    status_changed: "Durum değişti",
    email_parsed: "Mail eşleme",
    sms_sent: "SMS",
    whatsapp_sent: "WhatsApp",
};

const SEEN_KEY = "mbs_seen_notifications";

function loadSeen() {
    try {
        const raw = sessionStorage.getItem(SEEN_KEY);
        return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
        return new Set();
    }
}

function saveSeen(set) {
    try {
        sessionStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(0, 200)));
    } catch {
        /* ignore */
    }
}

function fmtTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("tr-TR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

export default function LiveNotificationCenter() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const btnRef = useRef(null);
    const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
    const lastTs = useRef(null);
    const seenIds = useRef(loadSeen());
    const bootstrapped = useRef(false);

    const mergeItem = useCallback((n) => {
        if (!n?.id) return;
        setItems((prev) => {
            const merged = [n, ...prev.filter((x) => x.id !== n.id)];
            return merged.slice(0, 50);
        });
    }, []);

    const onWsMessage = useCallback(
        (data) => {
            if (!data) return;
            const n = data.notification || (data.event_type ? data : null);
            if (!n?.id) return;
            if (!seenIds.current.has(n.id)) {
                seenIds.current.add(n.id);
                saveSeen(seenIds.current);
                mergeItem(n);
                const Icon = ICONS[n.event_type] || Bell;
                toast(n.title, {
                    description: n.message || EVENT_LABELS[n.event_type] || "",
                    icon: <Icon className="h-4 w-4 text-[#D4AF37]" />,
                    duration: 6000,
                });
            }
        },
        [mergeItem]
    );

    const { connected } = useWebSocket("notifications/", onWsMessage);

    const poll = useCallback(async () => {
        try {
            const data = await liveNotifications(lastTs.current);
            const fresh = data.items || [];
            const incremental = Boolean(lastTs.current);

            if (fresh.length) {
                lastTs.current = data.server_time || fresh[0]?.created_at;
            }

            if (!bootstrapped.current) {
                bootstrapped.current = true;
                if (fresh.length) {
                    setItems(fresh.slice(0, 50));
                    fresh.forEach((n) => seenIds.current.add(n.id));
                    saveSeen(seenIds.current);
                }
                return;
            }

            if (fresh.length) {
                setItems((prev) => {
                    const merged = [...fresh, ...prev];
                    const byId = new Map();
                    merged.forEach((x) => byId.set(x.id, x));
                    return Array.from(byId.values()).slice(0, 50);
                });
                fresh.forEach((n) => {
                    if (incremental && !seenIds.current.has(n.id)) {
                        seenIds.current.add(n.id);
                        saveSeen(seenIds.current);
                        const Icon = ICONS[n.event_type] || Bell;
                        toast(n.title, {
                            description: n.message || EVENT_LABELS[n.event_type] || "",
                            icon: <Icon className="h-4 w-4 text-[#D4AF37]" />,
                            duration: 6000,
                        });
                    }
                });
            }
        } catch {
            /* sessiz */
        }
    }, []);

    useEffect(() => {
        poll();
        const intervalTime = connected ? 60000 : 8000;
        const id = setInterval(poll, intervalTime);
        return () => clearInterval(id);
    }, [poll, connected]);

    useEffect(() => {
        if (!open || !btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        const panelW = 440;
        const panelH = Math.min(window.innerHeight * 0.72, 620);
        let left = rect.right - panelW;
        let top = rect.top - panelH - 8;
        if (left < 8) left = 8;
        if (top < 8) top = rect.bottom + 8;
        if (left + panelW > window.innerWidth - 8) {
            left = window.innerWidth - panelW - 8;
        }
        setPanelPos({ top, left });
    }, [open]);

    async function removeOne(id, e) {
        e.preventDefault();
        e.stopPropagation();
        try {
            await deleteNotification(id);
            setItems((prev) => prev.filter((x) => x.id !== id));
            seenIds.current.delete(id);
            saveSeen(seenIds.current);
        } catch {
            toast.error("Silinemedi");
        }
    }

    async function removeAll() {
        try {
            await clearNotifications();
            setItems([]);
            seenIds.current = new Set();
            saveSeen(seenIds.current);
            toast.success("Bildirimler temizlendi");
        } catch {
            toast.error("Temizlenemedi");
        }
    }

    const unread = items.length;

    const panel =
        open &&
        createPortal(
            <>
                <div className="fixed inset-0 z-[200]" onClick={() => setOpen(false)} aria-hidden />
                <div
                    role="dialog"
                    aria-label="Canlı bildirimler"
                    className="fixed z-[210] w-[min(440px,calc(100vw-16px))] max-h-[72vh] flex flex-col rounded-xl border border-white/10 bg-[#0d1424] shadow-2xl"
                    style={{ top: panelPos.top, left: panelPos.left }}
                >
                    <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2 shrink-0">
                        <div>
                            <span className="text-sm font-semibold text-white">Canlı bildirimler</span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                {connected ? (
                                    <>
                                        <Wifi className="h-3 w-3 text-emerald-400" />
                                        <span className="text-[10px] text-emerald-400">WebSocket bağlı — anlık push</span>
                                    </>
                                ) : (
                                    <>
                                        <WifiOff className="h-3 w-3 text-amber-400" />
                                        <span className="text-[10px] text-amber-400">Yedek polling (8 sn)</span>
                                    </>
                                )}
                            </div>
                        </div>
                        {items.length > 0 && (
                            <button
                                type="button"
                                onClick={removeAll}
                                className="text-[11px] text-rose-400 hover:text-rose-300 shrink-0"
                            >
                                Tümünü sil
                            </button>
                        )}
                    </div>
                    <div className="overflow-y-auto flex-1 min-h-0">
                        {items.length === 0 ? (
                            <div className="p-6 text-sm text-slate-500 text-center leading-relaxed">
                                Henüz bildirim yok.
                                <br />
                                <span className="text-[11px]">Evrak, ödeme ve durum değişiklikleri burada görünür.</span>
                            </div>
                        ) : (
                            <ul className="divide-y divide-white/[0.06]">
                                {items.map((n) => {
                                    const Icon = ICONS[n.event_type] || Bell;
                                    const label = EVENT_LABELS[n.event_type] || n.event_type || "Olay";
                                    const row = (
                                        <>
                                            <Icon className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                                    <span className="text-[10px] uppercase tracking-wider text-slate-500 bg-white/5 px-1.5 py-0.5 rounded">
                                                        {label}
                                                    </span>
                                                    {n.dosya_no ? (
                                                        <span className="text-[10px] font-mono text-[#D4AF37]/80 bg-[#D4AF37]/10 px-1.5 py-0.5 rounded">
                                                            {n.dosya_no}
                                                        </span>
                                                    ) : null}
                                                    <span className="text-[10px] text-slate-600 ml-auto">{fmtTime(n.created_at)}</span>
                                                </div>
                                                <div className="text-sm font-medium text-white leading-snug">{n.title}</div>
                                                {n.message ? (
                                                    <div className="text-xs text-slate-400 mt-1.5 leading-relaxed whitespace-pre-wrap break-words line-clamp-4">
                                                        {n.message}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => removeOne(n.id, e)}
                                                className="shrink-0 p-1 text-slate-500 hover:text-rose-400 self-start"
                                                title="Sil"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </>
                                    );
                                    return (
                                        <li key={n.id}>
                                            {n.href ? (
                                                <Link
                                                    to={n.href.startsWith("/") ? n.href : `/${n.href}`}
                                                    onClick={() => setOpen(false)}
                                                    className="flex gap-3 p-4 hover:bg-white/[0.04] transition-colors"
                                                >
                                                    {row}
                                                </Link>
                                            ) : (
                                                <div className="flex gap-3 p-4">{row}</div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </>,
            document.body
        );

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                className="relative h-9 w-9 rounded-md border border-white/10 hover:bg-white/5 flex items-center justify-center text-slate-300"
                title="Canlı bildirimler"
                aria-expanded={open}
            >
                <Bell className="h-4 w-4" />
                <span
                    className={`absolute bottom-0.5 right-0.5 h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-500" : "bg-amber-500"}`}
                    title={connected ? "WebSocket aktif" : "Polling modu"}
                />
                {unread > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-[#D4AF37] text-[10px] font-bold text-slate-900 flex items-center justify-center">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>
            {panel}
        </>
    );
}
